import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import ControlCameraIcon from "@mui/icons-material/ControlCamera";
import CircleIcon from "@mui/icons-material/Circle";
import CrisisAlertIcon from "@mui/icons-material/CrisisAlert";
import CropIcon from "@mui/icons-material/Crop";
import AssignmentIcon from "@mui/icons-material/Assignment";
import WbIncandescentIcon from "@mui/icons-material/WbIncandescent";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import EmailIcon from "@mui/icons-material/Email";

import FlipCard from "./FlipCard";
import LineBreak from "./LineBreak";
import partnershipsbg from "../assets/partnerships.jpg";
import businessPic from "../assets/partnerships/business.jpg";
import productPic from "../assets/partnerships/product.jpg";
import marketPic from "../assets/partnerships/market.jpg";

export default function PartnershipsSection(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const targetFieldsItems =
    (t("partnerships.targetFieldsItems", {
      returnObjects: true,
    }) as string[]) || [];
  const areaSupport =
    (t("partnerships.areaSupport", { returnObjects: true }) as any[]) || [];
  const areaImages = [businessPic, productPic, marketPic];
  const engagementModelsItems =
    (t("partnerships.engagementModelsItems", {
      returnObjects: true,
    }) as string[]) || [];
  const strengthsItems =
    (t("partnerships.strengthsItems", { returnObjects: true }) as string[]) ||
    [];
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        overflow: "visible", // allow text to exceed image height
      }}
    >
      {/* Background Image */}
      <Box
        sx={{
          height: { xs: 300, md: 500 },
          backgroundImage: `url(${partnershipsbg})`,
          backgroundSize: "cover",
          backgroundPosition: "50% 70%",
          opacity: 0.25,
        }}
      />

      {/* Overlapping Text */}
      <Box
        id="partnerships"
        sx={{
          pt: { xs: 14, md: 20 },
          px: { xs: 2, md: 0 },
          mt: { xs: "-200px", md: "-400px" }, // pulls text upward over image
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Card
          elevation={3}
          sx={{
            alignItems: "center",
            color: "white",
            width: { xs: "100%", lg: "60%" },
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              mt: 3,
              mx: "auto",
              width: { xs: "95%", md: "75%" },
              textAlign: "center",
            }}
          >
            <Typography variant="h4" color="text.primary" gutterBottom>
              {t("partnerships.title")}
            </Typography>
          </Box>
          <Box sx={{ mt: 2, mx: "auto", width: { xs: "95%", md: "75%" } }}>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <Trans
                i18nKey="partnerships.intro"
                components={{ br: <LineBreak /> }}
              />
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <ControlCameraIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("partnerships.positionTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <Trans
                i18nKey="partnerships.positionContent"
                components={{ br: <LineBreak /> }}
              />
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <CrisisAlertIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("partnerships.targetTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("partnerships.targetStart")}
            </Typography>
            <List dense>
              {targetFieldsItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("partnerships.targetEnd")}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ my: 2 }}
            >
              <CropIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("partnerships.areaTitle")}
            </Typography>
            <Grid container spacing={4} sx={{ justifyContent: "center" }}>
              {areaSupport.map((item, i) => (
                <Grid size={{ xs: 12, md: 6 }} key={item.title}>
                  <FlipCard
                    title={item.title}
                    image={areaImages[i]}
                    description={item.description}
                    aspectRatio={i18n.language === "en" ? "3 / 4" : "4 / 3"}
                  />
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <AssignmentIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("partnerships.experienceTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              <Trans
                i18nKey="partnerships.experienceContent"
                components={{ br: <LineBreak /> }}
              />
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <WbIncandescentIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("partnerships.engagementTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("partnerships.engagementStart")}
            </Typography>
            <List dense>
              {engagementModelsItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("partnerships.engagementEnd")}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <WorkspacePremiumIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("partnerships.strengthsTitle")}
            </Typography>
            <List dense>
              {strengthsItems.map((item) => (
                <ListItem key={item}>
                  <ListItemIcon>
                    <CircleIcon sx={{ mr: 1 }} />
                  </ListItemIcon>
                  <ListItemText primary={item} sx={{ color: "text.primary" }} />
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="h5"
              color="text.primary"
              gutterBottom
              sx={{ mt: 2 }}
            >
              <QuestionAnswerIcon sx={{ verticalAlign: "middle", mr: 1 }} />
              {t("partnerships.inquiryTitle")}
            </Typography>
            <Typography
              variant="body1"
              color="text.primary"
              sx={{ mt: 1, lineHeight: 1.2 }}
            >
              {t("partnerships.inquiryContent")}
            </Typography>
            <Box
              sx={{
                mt: 3,
                mx: "auto",
                width: { xs: "95%", md: "75%" },
                textAlign: "center",
              }}
            >
              <Button
                component="a"
                href="mailto:sharkshooter2011@gmail.com"
                endIcon={<EmailIcon />}
                size="large"
                variant="contained"
                sx={{
                  px: 5,
                  py: 1.8,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  borderRadius: 999,
                  textTransform: "none",
                  boxShadow: 3,
                  transition: "all 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 6,
                  },
                }}
              >
                {t("partnerships.contactBtnTitle")}
              </Button>
            </Box>
          </Box>
        </Card>
      </Box>
    </Box>
  );
}
